/**
 * React hook for acquisition-offer eligibility.
 *
 * Returns `{ eligible, loading, reason }`. Pattern matched off
 * `useApplyPromoCode` — same module conventions, fires on mount, single
 * network call.
 *
 * Defaults `eligible` to `true` while loading or on error so the CTA
 * is shown by default. The backend will reject the actual redemption
 * if the user is genuinely ineligible — we don't want to hide a
 * legitimate offer because of a slow API or a momentary network blip.
 *
 * Usage:
 *
 *   const { eligible, loading } = useOfferEligibility("adv50");
 *   if (!loading && !eligible) return null;
 *   return <PromoBanner offer="adv50" ... />;
 */

import { useEffect, useState } from "react";
import {
    AcquisitionOfferKey,
    EligibilityReason,
    checkOfferEligibility,
} from "@/controllers/offerEligibility";

interface UseOfferEligibilityResult {
    /** True = show the CTA. Defaults true while loading / on error (fail open). */
    eligible: boolean;
    /** True until the first response (success or error) has come back. */
    loading: boolean;
    /** Machine-readable reason once the response is in. `null` while loading. */
    reason: EligibilityReason | null;
    /** ISO timestamp of the most recent redemption inside the window, if any. */
    lastRedeemedAt: string | null;
}

const useOfferEligibility = (
    offerKey: AcquisitionOfferKey
): UseOfferEligibilityResult => {
    const [eligible, setEligible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [reason, setReason] = useState<EligibilityReason | null>(null);
    const [lastRedeemedAt, setLastRedeemedAt] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        checkOfferEligibility(offerKey)
            .then((res) => {
                if (cancelled) return;
                setEligible(res.eligible);
                setReason(res.reason);
                setLastRedeemedAt(res.last_redeemed_at);
            })
            .catch(() => {
                // Fail open — see module docstring on offerEligibility.ts.
                if (cancelled) return;
                setEligible(true);
                setReason(null);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [offerKey]);

    return { eligible, loading, reason, lastRedeemedAt };
};

export default useOfferEligibility;
